import React, { useState, useEffect, Fragment, useRef } from 'react';
import { contacts } from '../utils/api';
import { Dialog, Transition } from '@headlessui/react';
import { formatPhoneNumber } from '../utils/phoneFormat';

const SellerSelect = ({ value, onChange, className }) => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(
    sellers.find(s => s.id === value)?.display_name || ''
  );
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const componentRef = useRef(null);

  // Update dropdown position on scroll and open
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (componentRef.current) {
        const rect = componentRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 300; // Approximate max height of dropdown

        setDropdownPosition(spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'top' : 'bottom');
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  // Format display name to show company name in parentheses if it exists
  const formatDisplayName = (seller) => {
    const name = `${seller.first_name || ''} ${seller.last_name || ''}`.trim();
    if (seller.company_name) {
      return name ? `${name} (${seller.company_name})` : seller.company_name;
    }
    return name || 'Unknown';
  };

  // Fetch sellers and set initial search term
  useEffect(() => {
    const fetchSellers = async () => {
      try {
        // Get contacts that are sellers or buyer/sellers
        const data = await contacts.getAll({ type: 'seller' });
        // Add formatted display name to each seller
        const formattedData = data.map(seller => ({
          ...seller,
          display_name: formatDisplayName(seller)
        }));
        setSellers(formattedData);
        // Set initial search term if we have a value
        if (value) {
          const seller = formattedData.find(s => s.id === value);
          if (seller) {
            setSearchTerm(seller.display_name);
          }
        }
      } catch (err) {
        setError('Failed to load sellers');
        console.error('Failed to fetch sellers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSellers();
  }, [value]);

  const normalizeString = (str) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  }

  const filteredSellers = searchTerm
    ? sellers.filter(seller =>
        normalizeString(seller.display_name).includes(normalizeString(searchTerm))
      )
    : sellers;

  const selectedSeller = sellers.find(s => s.id === value);

  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContactData, setNewContactData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    secondary_address: '',
    source: '',
    contact_type: 'seller',
    notes: ''
  });

  const handleNewContactSubmit = async () => {
    try {
      const newContact = await contacts.create(newContactData);
      const formattedContact = {
        ...newContact,
        display_name: formatDisplayName(newContact)
      };
      setSellers(prevSellers => [...prevSellers, formattedContact]);
      onChange(newContact.id);
      setSearchTerm(newContact.display_name);
      setShowNewContactForm(false);
      setNewContactData({
        first_name: '',
        last_name: '',
        company_name: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        secondary_address: '',
        source: '',
        contact_type: 'seller',
        notes: ''
      });
    } catch (err) {
      console.error('Failed to create contact:', err);
      setError('Failed to create contact');
    }
  };

  const handleNewContactChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      setNewContactData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
    } else {
      setNewContactData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  return (
    <div className={`relative ${className}`} ref={componentRef}>
      {showNewContactForm && (
        <Transition appear show={showNewContactForm} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setShowNewContactForm(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <div className="flex justify-between items-start">
                      <Dialog.Title className="text-lg font-medium leading-6 text-gray-900">
                        Create New Contact
                      </Dialog.Title>
                      <button
                        type="button"
                        onClick={() => setShowNewContactForm(false)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <span className="sr-only">Close</span>
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div className="mt-4">
                      <p className="text-sm text-gray-500 mb-4">Either First/Last Name or Company Name is required</p>
                      
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                            First Name
                          </label>
                          <input
                            type="text"
                            name="first_name"
                            id="first_name"
                            value={newContactData.first_name}
                            onChange={handleNewContactChange}
                            className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>

                        <div>
                          <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                            Last Name
                          </label>
                          <input
                            type="text"
                            name="last_name"
                            id="last_name"
                            value={newContactData.last_name}
                            onChange={handleNewContactChange}
                            className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>

                        <div>
                          <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                            Company Name
                          </label>
                          <input
                            type="text"
                            name="company_name"
                            id="company_name"
                            value={newContactData.company_name}
                            onChange={handleNewContactChange}
                            className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                            Email
                          </label>
                          <input
                            type="email"
                            name="email"
                            id="email"
                            value={newContactData.email}
                            onChange={handleNewContactChange}
                            className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>

                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                            Phone
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            id="phone"
                            value={newContactData.phone}
                            onChange={handleNewContactChange}
                            className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>

                        <div>
                          <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                            Website
                          </label>
                          <input
                            type="text"
                            name="website"
                            id="website"
                            value={newContactData.website}
                            onChange={handleNewContactChange}
                            className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>

                        <div>
                          <label htmlFor="contact_type" className="block text-sm font-medium text-gray-700">
                            Contact Type *
                          </label>
                          <select
                            name="contact_type"
                            id="contact_type"
                            required
                            value={newContactData.contact_type}
                            onChange={handleNewContactChange}
                            className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          >
                            <option value="buyer">Buyer</option>
                            <option value="seller">Seller</option>
                            <option value="Buyer/Seller">Buyer/Seller</option>
                            <option value="auction_house">Auction House</option>
                          </select>
                        </div>

                        <div>
                          <label htmlFor="source" className="block text-sm font-medium text-gray-700">
                            Source
                          </label>
                          <input
                            type="text"
                            name="source"
                            id="source"
                            value={newContactData.source}
                            onChange={handleNewContactChange}
                            className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>

                      </div>
                    </div>

                    <div className="mt-4 space-y-4">
                      <div>
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                          Address
                        </label>
                        <textarea
                          name="address"
                          id="address"
                          value={newContactData.address}
                          onChange={handleNewContactChange}
                          rows="2"
                          className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                          Notes
                        </label>
                        <textarea
                          name="notes"
                          id="notes"
                          value={newContactData.notes}
                          onChange={handleNewContactChange}
                          rows="3"
                          className="mt-1 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        />
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setShowNewContactForm(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleNewContactSubmit}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Create Contact
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      )}
      <div 
        className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left border border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-300 sm:text-sm"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (isOpen && filteredSellers.length > 0) {
            switch (e.key) {
              case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                  prev < filteredSellers.length - 1 ? prev + 1 : prev
                );
                break;
              case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => 
                  prev > 0 ? prev - 1 : prev
                );
                break;
              case 'Enter':
                if (isOpen && filteredSellers.length > 0) {
                  e.preventDefault();
                  const selectedSeller = filteredSellers[highlightedIndex];
                  onChange(selectedSeller.id);
                  setSearchTerm(selectedSeller.display_name);
                  setIsOpen(false);
                }
                break;
              case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                break;
            }
          }
        }}
      >
        <input
          type="text"
          id="seller-search"
          name="seller-search"
          className="w-full border-none py-2 pl-3 pr-12 text-sm leading-5 text-gray-900 focus:ring-0 truncate"
          placeholder="Search sellers..."
          value={searchTerm}
          readOnly={false}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            onChange(null); // Clear the selected value when typing
            setIsOpen(true);
            setHighlightedIndex(0);
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
        />
        <div className="absolute inset-y-0 right-0 flex items-center">
          {/* Show clear button when there is text */}
          {searchTerm && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSearchTerm('');
              }}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
              </svg>
            </button>
          )}
          <span className="pr-1">
            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="none" stroke="currentColor">
              <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </div>
      </div>

      {isOpen && (
        <div 
          className="absolute z-50 bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm max-h-[50vh] overflow-hidden rounded-md"
          style={{
            width: '200px',
            [dropdownPosition === 'bottom' ? 'top' : 'bottom']: '100%',
            left: 0
          }}
        >
          <ul className="overflow-auto max-h-[calc(50vh-2.5rem)] py-1">
          {loading && (
            <li className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-500">
              Loading sellers...
            </li>
          )}
          {error && (
            <li className="relative cursor-default select-none py-2 pl-3 pr-9 text-red-500">
              {error}
            </li>
          )}
          {!loading && !error && filteredSellers.length === 0 && (
            <li className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-500">
              No sellers found
            </li>
          )}
          {!loading && !error && filteredSellers.map((seller) => (
            <li
              key={seller.id}
              className={`relative cursor-default select-none py-2 pl-3 pr-9 ${
                seller.id === value ? 'bg-indigo-600 text-white' : 
                filteredSellers.indexOf(seller) === highlightedIndex ? 'bg-indigo-100 text-gray-900' :
                'text-gray-900'
              } hover:bg-indigo-600 hover:text-white`}
              onClick={() => {
                onChange(seller.id);
                setSearchTerm(seller.display_name);
                setIsOpen(false);
              }}
            >
              {seller.display_name}
            </li>
          ))}
          </ul>
          {!loading && !error && (
            <div className="sticky bottom-0 border-t bg-white py-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowNewContactForm(true);
                  setIsOpen(false);
                }}
                className="w-full px-3 py-2 text-left text-indigo-600 hover:text-indigo-900 hover:bg-gray-50"
              >
                Create new seller
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SellerSelect;
